import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  FileDownloadOutlined as FileDownloadIcon,
  NotificationsOutlined as NotificationsIcon
} from '@mui/icons-material';
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton
} from '@mui/material';
import DetailsTable from '@northern.tech/common-ui/DetailsTable';
import Pagination from '@northern.tech/common-ui/Pagination';
import GeneralApi from '@northern.tech/store/api/general-api';
import { DEVICE_STATES, SORTING_OPTIONS } from '@northern.tech/store/constants';
import { useAppDispatch } from '@northern.tech/store/store';
import { getDevicesByStatus } from '@northern.tech/store/thunks';

import ActiveAlerts, {
  type ActiveAlertItem
} from './ActiveAlerts';

import AlertNotification, {
  ALERT_NOTIFICATION_RIGHT_OFFSET,
  ALERT_NOTIFICATION_WIDTH,
  type AlertNotificationItem
} from './AlertNotification';

import {
  evaluateAlertTransitions,
  type AlertTransitionSnapshot
} from './AlertTransition';

type DeviceStatus = {
  connection_status: string;
  device_id: string;
  ip: string;
  last_time: string;
  program_status: string;
  sender_timestamp?: string;
};

type MenderDeviceInfo = {
  device_id: string;
  device_name: string;
  device_type: string;
  store_name: string;
};

type MergedDeviceStatus = DeviceStatus & MenderDeviceInfo;

type ActiveAlertApiItem = {
  active_alert_started_at: string | null;
  connection_status: string;
  device_id: string;
  program_status: string;
};

type ActiveAlertsResponse = {
  items: ActiveAlertApiItem[];
  page: number;
  per_page: number;
  total: number;
};

type MenderDevice = {
  attributes?: Record<string, unknown> | unknown[];
  group?: string;
  id?: string;
  identity_data?: Record<string, string>;
  tags?: Record<string, unknown>;
};

type BackupStatus = {
  available: boolean;
  backup_time_utc: string | null;
  device_id: string;
  error: string | null;
};

type BackupStatusResponse = {
  items: BackupStatus[];
};

type BackupDownloadResponse = {
  expires_in: number;
  filename: string;
  url: string;
};

type RunBackupStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'SUCCESS'
  | 'FAILED'
  | 'TIMEOUT';

type RunBackupRequest = {
  request_id: string;
  device_id: string;
  status: RunBackupStatus;
  reason: string | null;
};

type RunBackupResponse = {
  result: string;
  request: RunBackupRequest;
};

const ACTIVE_ALERTS_REFRESH_INTERVAL = 5000;
const MENDER_DEVICE_INFO_REFRESH_INTERVAL = 60000;
const BACKUP_STATUS_REFRESH_INTERVAL = 120000;
const RUN_BACKUP_POLL_INTERVAL = 5000;
const DEVICE_TABLE_MIN_WIDTH = 1280;

const DEVICE_TABLE_RIGHT_RESERVED_WIDTH =
  ALERT_NOTIFICATION_WIDTH + ALERT_NOTIFICATION_RIGHT_OFFSET;

const valueToString = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(item => String(item)).join(', ');
  }

  if (value && typeof value === 'object' && 'value' in value) {
    return valueToString((value as { value?: unknown }).value);
  }

  return '';
};

const getAttrValue = (device: MenderDevice, name: string, scope?: string): string => {
  const { attributes } = device;

  if (!attributes) {
    return '';
  }

  if (Array.isArray(attributes)) {
    const attr = attributes.find(item => {
      if (!item || typeof item !== 'object') {
        return false;
      }

      const typedItem = item as { name?: string; scope?: string; value?: unknown };
      return typedItem.name === name && (!scope || typedItem.scope === scope);
    }) as { value?: unknown } | undefined;

    return valueToString(attr?.value);
  }

  const directValue = attributes[name];

  if (directValue !== undefined) {
    return valueToString(directValue);
  }

  const scopedKey = scope ? `${scope}-${name}` : name;
  const scopedValue = attributes[scopedKey];

  if (scopedValue !== undefined) {
    return valueToString(scopedValue);
  }

  return '';
};

const getTagValue = (device: MenderDevice, name: string): string => valueToString(device.tags?.[name]);

const buildMenderDeviceMap = (menderDevices: MenderDevice[]) => {
  const map: Record<string, MenderDeviceInfo> = {};

  menderDevices.forEach(device => {
    const deviceId =
      device.identity_data?.device_id ||
      device.identity_data?.mac ||
      getAttrValue(device, 'device_id', 'identity') ||
      getAttrValue(device, 'mac', 'identity');

    if (!deviceId) {
      return;
    }

    map[deviceId] = {
      device_id: deviceId,
      store_name: device.group || getAttrValue(device, 'group', 'system'),
      device_name: getTagValue(device, 'name') || getAttrValue(device, 'name', 'tags'),
      device_type: getAttrValue(device, 'device_type', 'inventory')
    };
  });

  return map;
};

const JST_OFFSET_MILLISECONDS = 9 * 60 * 60 * 1000;

const formatActiveAlertTime = (
  value: string | null
): string => {
  if (!value) {
    return 'Unknown';
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return 'Unknown';
  }

  const jstDate = new Date(
    parsedDate.getTime() + JST_OFFSET_MILLISECONDS
  );

  const pad = (number: number) =>
    String(number).padStart(2, '0');

  return [
    `${jstDate.getUTCFullYear()}-${pad(
      jstDate.getUTCMonth() + 1
    )}-${pad(jstDate.getUTCDate())}`,
    `${pad(jstDate.getUTCHours())}:${pad(
      jstDate.getUTCMinutes()
    )}:${pad(jstDate.getUTCSeconds())}`
  ].join(' ');
};

const DeviceMonitor = () => {
  const dispatch = useAppDispatch();

  const [devices, setDevices] = useState<MergedDeviceStatus[]>([]);
  const [backupStatuses, setBackupStatuses] = useState<Record<string, BackupStatus>>({});
  const [backupStatusRefreshKey, setBackupStatusRefreshKey] = useState(0);
  const [downloadingDeviceId, setDownloadingDeviceId] = useState('');
  const [error, setError] = useState<string>('');
  const [backupError, setBackupError] = useState<string>('');
  const [runBackupRequests, setRunBackupRequests] =
    useState<Record<string, RunBackupRequest>>({});
  const [startingBackupDeviceIds, setStartingBackupDeviceIds] =
    useState<Record<string, boolean>>({});

  const [runBackupResultDialog, setRunBackupResultDialog] = useState({
    open: false,
    message: ''
  });
  const [searchText, setSearchText] = useState('');
  const [activeAlertsOpen, setActiveAlertsOpen] = useState(
    () => new URLSearchParams(window.location.search).get('open') === 'true'
  );
  const [activeAlertApiItems, setActiveAlertApiItems] =
    useState<ActiveAlertApiItem[]>([]);

  const [activeAlertsTotal, setActiveAlertsTotal] =
    useState(0);

  const [activeAlertsPage, setActiveAlertsPage] =
    useState(1);

  const [
    activeAlertsRowsPerPage,
    setActiveAlertsRowsPerPage
  ] = useState(10);

  const [activeAlertsError, setActiveAlertsError] =
    useState('');

  const [
    menderDeviceInfoError,
    setMenderDeviceInfoError
  ] = useState('');

  const [
    menderDeviceInfoMap,
    setMenderDeviceInfoMap
  ] = useState<Record<string, MenderDeviceInfo>>({});
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sort, setSort] = useState<{ direction: string; key: string }>({
    key: 'store_name',
    direction: SORTING_OPTIONS.asc
  });

  // TEMP: Alert Notification UI test.
  // Remove this block after the Alert Notification UI test is complete.
  const [testAlerts, setTestAlerts] = useState<AlertNotificationItem[]>([]);
  const testAlertCounterRef = useRef(0);

  const handleAddTestAlert = () => {
    const sequence = testAlertCounterRef.current + 1;
    testAlertCounterRef.current = sequence;

    const patternIndex = (sequence - 1) % 4;

    const newAlert: AlertNotificationItem = {
      deviceName: `Test Device ${sequence}`,
      id: `test-alert-${Date.now()}-${sequence}`,
      programStatus:
        patternIndex === 2 || patternIndex === 3
          ? 'UNKNOWN'
          : 'STOPPED'
    };

    if (patternIndex === 1 || patternIndex === 3) {
      newAlert.connectionStatus = 'DISCONNECT';
    }

    setTestAlerts(current => [
      ...current,
      newAlert
    ]);
  };
  // END TEMP: Alert Notification UI test.

  const [notificationAlerts, setNotificationAlerts] =
    useState<AlertNotificationItem[]>([]);

  const previousAlertStateRef = useRef<
    Record<string, AlertTransitionSnapshot> | null
  >(null);

  useEffect(() => {
    let cancelled = false;

    const fetchMenderDeviceInfo = async () => {
      try {
        const menderResults = await dispatch(
          getDevicesByStatus({
            status: DEVICE_STATES.accepted,
            page: 1,
            perPage: 2000,
            fetchAuth: false,
            selectedAttributes: [
              {
                scope: 'identity',
                attribute: 'device_id'
              },
              {
                scope: 'identity',
                attribute: 'mac'
              },
              {
                scope: 'system',
                attribute: 'group'
              },
              {
                scope: 'tags',
                attribute: 'name'
              },
              {
                scope: 'inventory',
                attribute: 'device_type'
              }
            ]
          })
        ).unwrap();

        if (cancelled) {
          return;
        }

        const lastResult =
          menderResults[menderResults.length - 1];

        const menderDevicesById =
          lastResult?.deviceAccu?.devicesById || {};

        const menderDevices = Object.values(
          menderDevicesById
        ) as MenderDevice[];

        setMenderDeviceInfoMap(
          buildMenderDeviceMap(menderDevices)
        );

        setMenderDeviceInfoError('');
      } catch {
        if (!cancelled) {
          setMenderDeviceInfoError(
            'Mender device information could not be loaded.'
          );
        }
      }
    };

    void fetchMenderDeviceInfo();

    const timer = window.setInterval(() => {
      void fetchMenderDeviceInfo();
    }, MENDER_DEVICE_INFO_REFRESH_INTERVAL);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const statusResponse = await fetch(
          '/device-monitor-api/status'
        );

        if (!statusResponse.ok) {
          throw new Error(
            `Failed to fetch monitor status: ${statusResponse.status}`
          );
        }

        const statusData: DeviceStatus[] =
          await statusResponse.json();

        if (cancelled) {
          return;
        }

        const mergedDevices = statusData.map(device => {
          const menderInfo =
            menderDeviceInfoMap[device.device_id];

          return {
            ...device,
            store_name: menderInfo?.store_name || '',
            device_name: menderInfo?.device_name || '',
            device_type: menderInfo?.device_type || ''
          };
        });

        const transitionResult =
          evaluateAlertTransitions(
            previousAlertStateRef.current,
            mergedDevices
          );

        previousAlertStateRef.current =
          transitionResult.nextState;

        if (transitionResult.alerts.length > 0) {
          setNotificationAlerts(
            transitionResult.alerts
          );
        }

        setDevices(mergedDevices);
        setError('');
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to fetch device monitor status'
          );
        }
      }
    };

    void fetchStatus();

    const timer = window.setInterval(() => {
      void fetchStatus();
    }, 5000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [menderDeviceInfoMap]);

  const backupDeviceIdsKey = useMemo(() => {
    const deviceIds = devices
      .map(device => device.device_id)
      .filter(Boolean);

    return [...new Set(deviceIds)].sort().join(',');
  }, [devices]);

  useEffect(() => {
    const deviceIds = backupDeviceIdsKey ? backupDeviceIdsKey.split(',') : [];

    if (deviceIds.length === 0) {
      setBackupStatuses({});
      setBackupError('');
      return;
    }

    let cancelled = false;

    const fetchBackupStatuses = async () => {
      try {
        const response = await GeneralApi.post<BackupStatusResponse>(
          '/device-monitor-api/backups/status',
          {
            device_ids: deviceIds
          }
        );

        const items = Array.isArray(response.data?.items) ? response.data.items : [];

        const nextStatuses = items.reduce<Record<string, BackupStatus>>((result, item) => {
          result[item.device_id] = item;
          return result;
        }, {});

        if (!cancelled) {
          setBackupStatuses(nextStatuses);
          setBackupError('');
        }
      } catch {
        if (!cancelled) {
          setBackupError('Backup status could not be loaded.');
        }
      }
    };

    void fetchBackupStatuses();

    const timer = window.setInterval(() => {
      void fetchBackupStatuses();
    }, BACKUP_STATUS_REFRESH_INTERVAL);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [backupDeviceIdsKey, backupStatusRefreshKey]);

  useEffect(() => {
    let cancelled = false;

    const fetchActiveAlerts = async () => {
      try {
        const response = await fetch(
          '/device-monitor-api/active-alerts' +
            `?page=${activeAlertsPage}` +
            `&per_page=${activeAlertsRowsPerPage}`
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch Active Alerts: ${response.status}`
          );
        }

        const data: ActiveAlertsResponse =
          await response.json();

        if (cancelled) {
          return;
        }

        const items = Array.isArray(data.items)
          ? data.items
          : [];

        const total = Math.max(
          0,
          Number(data.total) || 0
        );

        const maxPage = Math.max(
          1,
          Math.ceil(
            total / activeAlertsRowsPerPage
          )
        );

        setActiveAlertsTotal(total);
        setActiveAlertsError('');

        if (activeAlertsPage > maxPage) {
          setActiveAlertApiItems([]);
          setActiveAlertsPage(maxPage);
          return;
        }

        setActiveAlertApiItems(items);
      } catch (err) {
        if (!cancelled) {
          setActiveAlertsError(
            err instanceof Error
              ? err.message
              : 'Failed to fetch Active Alerts'
          );
        }
      }
    };

    void fetchActiveAlerts();

    const timer = window.setInterval(() => {
      void fetchActiveAlerts();
    }, ACTIVE_ALERTS_REFRESH_INTERVAL);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [
    activeAlertsPage,
    activeAlertsRowsPerPage
  ]);

  const activeAlerts = useMemo<
    ActiveAlertItem[]
  >(
    () =>
      activeAlertApiItems.map(item => ({
        connectionStatus:
          item.connection_status || '-',
        deviceId: item.device_id,
        deviceName:
          menderDeviceInfoMap[item.device_id]
            ?.device_name || '-',
        firstAbnormalTime:
          formatActiveAlertTime(
            item.active_alert_started_at
          ),
        programStatus:
          item.program_status || '-'
      })),
    [
      activeAlertApiItems,
      menderDeviceInfoMap
    ]
  );

  const handleOpenActiveAlerts = () => {
    setActiveAlertsPage(1);
    setActiveAlertsOpen(true);
  };

  const handleActiveAlertsPagination = (
    newPage: number,
    newRowsPerPage = activeAlertsRowsPerPage
  ) => {
    setActiveAlertsPage(newPage);
    setActiveAlertsRowsPerPage(
      newRowsPerPage
    );
  };

  const handleBackupDownload = useCallback(async (deviceId: string) => {
    setDownloadingDeviceId(deviceId);
    setBackupError('');

    try {
      const response = await GeneralApi.get<BackupDownloadResponse>(
        `/device-monitor-api/backups/${encodeURIComponent(deviceId)}/download`
      );

      const downloadUrl = response.data?.url;

      if (!downloadUrl) {
        throw new Error('Download URL was not returned.');
      }

      window.location.assign(downloadUrl);
    } catch {
      setBackupError('Backup download failed.');
    } finally {
      setDownloadingDeviceId(currentDeviceId => (currentDeviceId === deviceId ? '' : currentDeviceId));
    }
  }, []);

  const handleRunBackup = useCallback(async (deviceId: string) => {
    setBackupError('');

    setStartingBackupDeviceIds(current => ({
      ...current,
      [deviceId]: true
    }));

    try {
      const response = await GeneralApi.post<RunBackupResponse>(
        `/device-monitor-api/backups/${encodeURIComponent(deviceId)}/run`,
        {}
      );

      const request = response.data?.request;

      if (!request?.request_id) {
        throw new Error('Run Backup request ID was not returned.');
      }

      setRunBackupRequests(current => ({
        ...current,
        [deviceId]: request
      }));
    } catch {
      setRunBackupResultDialog({
        open: true,
        message: 'バックアップを開始できませんでした。'
      });
    } finally {
      setStartingBackupDeviceIds(current => {
        const next = { ...current };
        delete next[deviceId];
        return next;
      });
    }
  }, []);

  useEffect(() => {
    const activeRequests = Object.values(
      runBackupRequests
    ).filter(
      request =>
        request.status === 'PENDING' ||
        request.status === 'RUNNING'
    );

    if (activeRequests.length === 0) {
      return;
    }

    let cancelled = false;

    const pollRunBackupStatuses = async () => {
      for (const currentRequest of activeRequests) {
        try {
          const response =
            await GeneralApi.get<RunBackupResponse>(
              `/device-monitor-api/backups/run/${encodeURIComponent(
                currentRequest.request_id
              )}`
            );

          if (cancelled) {
            return;
          }

          const request = response.data?.request;

          if (!request?.request_id) {
            continue;
          }

          const isFinished =
            request.status === 'SUCCESS' ||
            request.status === 'FAILED' ||
            request.status === 'TIMEOUT';

          setRunBackupRequests(current => {
            const storedRequest =
              current[request.device_id];

            if (
              !storedRequest ||
              storedRequest.request_id !== request.request_id
            ) {
              return current;
            }

            if (isFinished) {
              const next = { ...current };
              delete next[request.device_id];
              return next;
            }

            if (
              storedRequest.status === request.status &&
              storedRequest.reason === request.reason
            ) {
              return current;
            }

            return {
              ...current,
              [request.device_id]: request
            };
          });

          if (isFinished) {
            let message =
              'バックアップ結果を確認できませんでした。';

            if (request.status === 'SUCCESS') {
              message = 'バックアップが完了しました。';

              setBackupStatusRefreshKey(current =>
                current + 1
              );
            } else if (request.status === 'FAILED') {
              message = 'バックアップに失敗しました。';
            }

            setRunBackupResultDialog({
              open: true,
              message
            });
          }
        } catch {
          // 一時的に状態取得に失敗しても、
          // Run Backup自体をFAILEDにはしない。
        }
      }
    };

    const timer = window.setInterval(() => {
      void pollRunBackupStatuses();
    }, RUN_BACKUP_POLL_INTERVAL);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [runBackupRequests]);

  const columns = useMemo(
    () => [
      {
        key: 'store_name',
        title: 'Store',
        cellProps: {
          style: {
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            width: '10%'
          }
        },
        render: (device: MergedDeviceStatus) => {
          const value = device.store_name || '-';

          return (
            <span
              title={value}
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {value}
            </span>
          );
        },
        sortable: true
      },
      {
        key: 'device_name',
        title: 'Device name',
        cellProps: {
          style: {
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            width: '12%'
          }
        },
        render: (device: MergedDeviceStatus) => {
          const value = device.device_name || '-';

          return (
            <span
              title={value}
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {value}
            </span>
          );
        },
        sortable: true
      },
      {
        key: 'device_id',
        title: 'Device ID',
        cellProps: {
          style: {
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            width: '11%'
          }
        },
        render: (device: MergedDeviceStatus) => {
          const value = device.device_id || '-';

          return (
            <span
              title={value}
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {value}
            </span>
          );
        },
        sortable: true
      },
      {
        key: 'device_type',
        title: 'Device type',
        cellProps: {
          style: {
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            width: '9%'
          }
        },
        render: (device: MergedDeviceStatus) =>
          device.device_type || '-',
        sortable: true
      },
      {
        key: 'ip',
        title: 'IP address',
        cellProps: {
          style: {
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            width: '8%'
          }
        },
        render: (device: MergedDeviceStatus) =>
          device.ip || '-',
        sortable: true
      },
      {
        key: 'connection_status',
        title: 'Connection',
        cellProps: {
          style: {
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            width: '9%'
          }
        },
        render: (device: MergedDeviceStatus) =>
          device.connection_status || '-',
        sortable: true
      },
      {
        key: 'program_status',
        title: 'Program',
        cellProps: {
          style: {
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            width: '8%'
          }
        },
        render: (device: MergedDeviceStatus) =>
          device.program_status || '-',
        sortable: true
      },
      {
        key: 'last_time',
        title: 'Last received',
        cellProps: {
          style: {
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            width: '12%'
          }
        },
        render: (device: MergedDeviceStatus) => {
          const value =
            device.last_time ||
            device.sender_timestamp ||
            '-';

          return (
            <span
              title={value}
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {value}
            </span>
          );
        },
        sortable: true
      },
      {
        key: 'run_backup',
        title: 'Run Backup',
        cellProps: {
          align: 'center',
          style: {
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            width: '8%'
          }
        },
        render: (device: MergedDeviceStatus) => {
          const request =
            runBackupRequests[device.device_id];

          const isStarting =
            startingBackupDeviceIds[device.device_id] === true;

          const isPending =
            request?.status === 'PENDING';

          const isRunning =
            request?.status === 'RUNNING';

          const isActive =
            isStarting || isPending || isRunning;

          const isConnected =
            device.connection_status === 'CONNECT';

          let label = 'BACKUP';

          if (isStarting || isPending) {
            label = 'PENDING...';
          } else if (isRunning) {
            label = 'RUNNING...';
          }

          return (
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                width: '100%'
              }}
            >
              <Button
                size="small"
                variant="outlined"
                disabled={!isConnected || isActive}
                onClick={event => {
                  event.stopPropagation();
                  void handleRunBackup(
                    device.device_id
                  );
                }}
              >
                {label}
              </Button>
            </div>
          );
        },
        sortable: false
      },
      {
        key: 'backup_status',
        title: 'Backup Status',
        cellProps: {
          align: 'center',
          style: {
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            width: '8%'
          }
        },
        render: (device: MergedDeviceStatus) => {
          const status =
            backupStatuses[device.device_id];

          let backgroundColor = '#9e9e9e';
          let label = 'Backup status loading';

          if (status?.error) {
            label = 'Backup status unavailable';
          } else if (status?.available) {
            backgroundColor = '#00C853';
            label = status.backup_time_utc
              ? `Backup available: ${status.backup_time_utc}`
              : 'Backup available';
          } else if (status) {
            backgroundColor = '#FF1744';
            label = 'No backup available';
          }

          return (
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                width: '100%'
              }}
            >
              <span
                role="img"
                aria-label={label}
                title={label}
                style={{
                  backgroundColor,
                  borderRadius: '50%',
                  display: 'inline-block',
                  height: '10px',
                  width: '10px'
                }}
              />
            </div>
          );
        },
        sortable: false
      },
      {
        key: 'backup_download',
        title: 'Backup Download',
        cellProps: {
          align: 'center',
          style: {
            verticalAlign: 'middle',
            whiteSpace: 'nowrap',
            width: '10%'
          }
        },
        render: (device: MergedDeviceStatus) => {
          const status =
            backupStatuses[device.device_id];

          if (!status?.available || status.error) {
            return (
              <div
                style={{
                  textAlign: 'center',
                  width: '100%'
                }}
              >
                -
              </div>
            );
          }

          const isDownloading =
            downloadingDeviceId === device.device_id;

          return (
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                width: '100%'
              }}
            >
              <IconButton
                size="small"
                disabled={isDownloading}
                aria-label={`Download backup for ${device.device_id}`}
                title="Download backup"
                onClick={event => {
                  event.stopPropagation();
                  void handleBackupDownload(
                    device.device_id
                  );
                }}
              >
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
        sortable: false
      }
    ],
    [
      backupStatuses,
      downloadingDeviceId,
      handleBackupDownload,
      handleRunBackup,
      runBackupRequests,
      startingBackupDeviceIds
    ]
  );

  const onChangePagination = (newPage: number, newRowsPerPage = rowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const onChangeSorting = (sortKey: string) => {
    const direction =
      sort.key === sortKey && sort.direction === SORTING_OPTIONS.asc
        ? SORTING_OPTIONS.desc
        : SORTING_OPTIONS.asc;

    setSort({
      key: sortKey,
      direction
    });

    setPage(1);
  };

  const keyword = searchText.trim().toLowerCase();

  const filteredDevices = devices.filter(device => {
    if (!keyword) {
      return true;
    }

    return [
      device.store_name,
      device.device_name,
      device.device_id,
      device.device_type
    ]
      .join(' ')
      .toLowerCase()
      .includes(keyword);
  });

  const sortedDevices = [...filteredDevices].sort((a, b) => {
    const aValue = String(a[sort.key as keyof MergedDeviceStatus] ?? '').toLowerCase();
    const bValue = String(b[sort.key as keyof MergedDeviceStatus] ?? '').toLowerCase();

    if (aValue < bValue) {
      return sort.direction === SORTING_OPTIONS.asc ? -1 : 1;
    }

    if (aValue > bValue) {
      return sort.direction === SORTING_OPTIONS.asc ? 1 : -1;
    }

    return 0;
  });

  const pagedDevices = sortedDevices.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <div className="margin-left-large margin-right-large margin-top-large">
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between'
        }}
      >
        <h1>Device Monitor</h1>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginRight: `${ALERT_NOTIFICATION_RIGHT_OFFSET - 48}px`,
            width: `${ALERT_NOTIFICATION_WIDTH}px`
          }}
        >
          <Badge
            badgeContent={activeAlertsTotal}
            color="error"
            max={9999}
            showZero
          >
            <Button
              startIcon={<NotificationsIcon />}
              style={{
                fontSize: '24px',
                height: '56px',
                textTransform: 'none',
                width: '260px'
              }}
              variant="outlined"
              onClick={handleOpenActiveAlerts}
            >
              Active Alerts
            </Button>
          </Badge>
        </div>
      </div>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          marginBottom: '16px',
          marginTop: '24px'
        }}
      >
        <input
          type="text"
          value={searchText}
          onChange={event => {
            setSearchText(event.target.value);
            setPage(1);
          }}
          placeholder="Search by Store, Device name, Device ID, or Device type"
          style={{
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '10px 12px',
            width: '360px'
          }}
        />

        {/* TEMP: Remove after Alert Notification UI test. */}
        <Button
          size="small"
          style={{ marginLeft: '12px' }}
          variant="outlined"
          onClick={handleAddTestAlert}
        >
          ADD TEST ALERT
        </Button>
      </div>

      {error && (
        <div style={{ color: 'red', marginTop: '16px' }}>
          Error: {error}
        </div>
      )}

      {menderDeviceInfoError && (
        <div
          className="muted"
          style={{ marginTop: '8px' }}
        >
          {menderDeviceInfoError}
        </div>
      )}

      {backupError && (
        <div className="muted" style={{ marginTop: '8px' }}>
          {backupError}
        </div>
      )}

      {devices.length === 0 ? (
        <p className="margin-top muted">No device status found.</p>
      ) : filteredDevices.length === 0 ? (
        <p className="margin-top muted">No matching device found.</p>
      ) : (
        <div
          style={{
            marginRight: `${DEVICE_TABLE_RIGHT_RESERVED_WIDTH}px`
          }}
        >
          <div
            style={{
              overflowX: 'auto'
            }}
          >
            <DetailsTable
              columns={columns}
              items={pagedDevices}
              sort={sort}
              onChangeSorting={onChangeSorting}
              onItemClick={undefined}
              tableRef={undefined}
              style={{
                minWidth: `${DEVICE_TABLE_MIN_WIDTH}px`,
                tableLayout: 'fixed',
                width: '100%'
              }}
            />
          </div>

          <div className="flexbox">
            <Pagination
              className="margin-top-none"
              count={filteredDevices.length}
              rowsPerPage={rowsPerPage}
              onChangePage={onChangePagination}
              onChangeRowsPerPage={newRowsPerPage =>
                onChangePagination(1, newRowsPerPage)
              }
              page={page}
            />
          </div>
        </div>
      )}
      {activeAlertsError && (
        <div
          className="muted"
          style={{ marginTop: '8px' }}
        >
          {activeAlertsError}
        </div>
      )}
      <ActiveAlerts
        alerts={activeAlerts}
        open={activeAlertsOpen}
        page={activeAlertsPage}
        rowsPerPage={activeAlertsRowsPerPage}
        total={activeAlertsTotal}
        onChangePagination={
          handleActiveAlertsPagination
        }
        onClose={() =>
          setActiveAlertsOpen(false)
        }
      />
      <Dialog
        open={runBackupResultDialog.open}
        onClose={() =>
          setRunBackupResultDialog({
            open: false,
            message: ''
          })
        }
      >
        <DialogTitle>バックアップ</DialogTitle>

        <DialogContent>
          {runBackupResultDialog.message}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setRunBackupResultDialog({
                open: false,
                message: ''
              })
            }
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
      <AlertNotification
        alerts={[
          ...notificationAlerts,
          ...testAlerts
        ]}
      />
    </div>
  );
};

export default DeviceMonitor;
